import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../../services/api';
import { 
  Ticket, Plus, Trash2, Calendar, DollarSign, Percent, 
  AlertCircle, Loader2, X, Info, User, Edit3, Search, UserCheck, Sparkles, Bike
} from 'lucide-react';
import toast from 'react-hot-toast';

const Cupons = ({ estabelecimentoId }) => {
  const [cupons, setCupons] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCupomId, setSelectedCupomId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [buscaCupom, setBuscaCupom] = useState('');
  const [buscaClienteModal, setBuscaClienteModal] = useState('');

  const [novoCupom, setNovoCupom] = useState({
    codigo: '', tipo: 'porcentagem', valor: '', valor_minimo: '0',
    data_validade: '', limite_usos: '', cliente_id: '', ativo: 1
  });

  const carregarDados = useCallback(async () => {
    setLoading(true);
    try {
      const [resCupons, resClientes] = await Promise.all([
        api.get(`/marketing/cupons/${estabelecimentoId}`),
        api.get(`/clientes/${estabelecimentoId}`) // Rota administrativa corrigida
      ]);
      setCupons(resCupons.data || []);
      setClientes(resClientes.data || []);
    } catch (err) {
      console.error("Erro ao buscar dados:", err);
      toast.error("Erro ao carregar informações.");
    } finally {
      setLoading(false);
    }
  }, [estabelecimentoId]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const cuponsFiltrados = useMemo(() => {
    return cupons.filter(c => 
      c.codigo.toLowerCase().includes(buscaCupom.toLowerCase())
    );
  }, [cupons, buscaCupom]);

    const clientesFiltrados = useMemo(() => {
        if (!buscaClienteModal) return [];
        
        const termoBusca = buscaClienteModal.toLowerCase().trim();
        const termoLimpo = termoBusca.replace(/\D/g, ''); // Apenas números para testar documentos
    
        return clientes.filter(c => {
          // Verifica nome e whatsapp de forma segura
          const nomeMatch = c.nome?.toLowerCase().includes(termoBusca);
          const whatsMatch = c.whatsapp?.includes(termoBusca);
          
          // Checa todas as possíveis variações da coluna de CPF/Documento vindas do banco
          const valorCpf = c.cpf || c.documento || c.nu_cpf || '';
          const cpfDoBancoLimpo = String(valorCpf).replace(/\D/g, '');
          const cpfMatch = termoLimpo.length > 0 && cpfDoBancoLimpo.includes(termoLimpo);
    
          return nomeMatch || whatsMatch || cpfMatch;
        }).slice(0, 5);
      }, [clientes, buscaClienteModal]);

  const handleOpenEdit = (cupom) => {
    const clienteEncontrado = clientes.find(c => c.id === cupom.cliente_id);
    setBuscaClienteModal(clienteEncontrado ? clienteEncontrado.nome : '');
    setNovoCupom({
      ...cupom,
      data_validade: cupom.data_validade ? cupom.data_validade.split('T')[0] : '',
      cliente_id: cupom.cliente_id || ''
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setBuscaClienteModal('');
    setNovoCupom({ codigo: '', tipo: 'porcentagem', valor: '', valor_minimo: '0', data_validade: '', limite_usos: '', cliente_id: '', ativo: 1 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await api.put(`/marketing/cupons/${novoCupom.id}`, novoCupom);
        toast.success("Cupom atualizado!");
      } else {
        await api.post('/marketing/cupons', { ...novoCupom, estabelecimento_id: estabelecimentoId });
        toast.success("Cupom criado!");
      }
      handleCloseModal();
      carregarDados();
    } catch (err) {
      toast.error(err.response?.data?.error || "Erro ao salvar cupom.");
    }
  };

  const confirmarExclusao = async () => {
    try {
      await api.delete(`/marketing/cupons/${selectedCupomId}`);
      toast.success("Cupom removido.");
      setShowDeleteModal(false);
      carregarDados();
    } catch (err) {
      toast.error("Erro ao excluir o cupom.");
    }
  };

  return (
    <div style={{ padding: '25px', background: '#f8fafc', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
      
      {/* HEADER COMPACTO */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '900', color: '#0f172a', fontSize: '24px', margin: 0 }}>
            <div style={{ background: '#ef4444', padding: '8px', borderRadius: '12px', color: '#fff' }}>
              <Ticket size={24} />
            </div>
            Central de Promoções
          </h2>
          <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>Gerencie cupons e frete grátis</p>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input 
              type="text" 
              placeholder="Buscar..." 
              value={buscaCupom}
              onChange={(e) => setBuscaCupom(e.target.value)}
              style={{ padding: '10px 12px 10px 35px', borderRadius: '12px', border: '1px solid #e2e8f0', outline: 'none', width: '180px', fontSize: '14px' }}
            />
          </div>
          <button 
            onClick={() => setShowModal(true)} 
            style={{ 
              background: '#0f172a', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '12px', 
              display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '800', fontSize: '14px'
            }}
          >
            <Plus size={18} /> Novo Cupom
          </button>
        </div>
      </header>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '100px' }}><Loader2 className="animate-spin" size={40} color="#ef4444" /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
          {cuponsFiltrados.map(cupom => (
            <div key={cupom.id} style={{ 
              background: '#fff', borderRadius: '20px', border: '1px solid #e2e8f0', position: 'relative', overflow: 'hidden',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', opacity: cupom.ativo ? 1 : 0.6
            }}>
              {/* Recortes de Ticket */}
              <div style={{ position: 'absolute', top: '65px', left: '-10px', width: '20px', height: '20px', background: '#f8fafc', borderRadius: '50%', border: '1px solid #e2e8f0' }}></div>
              <div style={{ position: 'absolute', top: '65px', right: '-10px', width: '20px', height: '20px', background: '#f8fafc', borderRadius: '50%', border: '1px solid #e2e8f0' }}></div>

              <div style={{ padding: '15px 20px', borderBottom: '1px dashed #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontSize: '10px', fontWeight: '800', color: '#94a3b8' }}>CÓDIGO</span>
                  <div style={{ fontSize: '16px', fontWeight: '900', color: '#ef4444' }}>{cupom.codigo}</div>
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button onClick={() => handleOpenEdit(cupom)} style={{ background: '#f1f5f9', border: 'none', padding: '6px', borderRadius: '8px', cursor: 'pointer' }}><Edit3 size={14}/></button>
                  <button onClick={() => { setSelectedCupomId(cupom.id); setShowDeleteModal(true); }} style={{ background: '#fff1f2', border: 'none', color: '#ef4444', padding: '6px', borderRadius: '8px', cursor: 'pointer' }}><Trash2 size={14} /></button>
                </div>
              </div>

              <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                  <span style={{ fontSize: '28px', fontWeight: '900', color: '#0f172a' }}>
                    {cupom.tipo === 'frete_gratis' ? <Bike size={28} /> : (cupom.tipo === 'porcentagem' ? `${cupom.valor}%` : `R$ ${cupom.valor}`)}
                  </span>
                  <span style={{ fontSize: '12px', fontWeight: '800', color: '#64748b' }}>
                    {cupom.tipo === 'frete_gratis' ? 'FRETE GRÁTIS' : 'OFF'}
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '12px', textAlign: 'center' }}>
                    <div style={{ color: '#94a3b8', fontSize: '9px', fontWeight: '800' }}>MÍNIMO</div>
                    <div style={{ fontWeight: '800', color: '#334155', fontSize: '12px' }}>R$ {cupom.valor_minimo}</div>
                  </div>
                  <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '12px', textAlign: 'center' }}>
                    <div style={{ color: '#94a3b8', fontSize: '9px', fontWeight: '800' }}>USOS</div>
                    <div style={{ fontWeight: '800', color: '#334155', fontSize: '12px' }}>{cupom.usos_atuais}/{cupom.limite_usos || '∞'}</div>
                  </div>
                </div>

                {cupom.cliente_id && (
                  <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '6px', color: '#2563eb', fontSize: '11px', fontWeight: '800', background: '#eff6ff', padding: '6px 10px', borderRadius: '8px' }}>
                    <UserCheck size={12} /> CLIENTE EXCLUSIVO
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL DE CRIAÇÃO / EDIÇÃO */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: '#fff', padding: '30px', borderRadius: '24px', width: '100%', maxWidth: '420px', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '900', color: '#0f172a' }}>{isEditing ? 'Editar Cupom' : 'Novo Benefício'}</h3>
                <button onClick={handleCloseModal} style={{ background: '#f1f5f9', border: 'none', borderRadius: '10px', padding: '6px' }}><X size={20}/></button>
            </div>
            
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#64748b', marginBottom: '5px' }}>CÓDIGO DO CUPOM</label>
                <input type="text" placeholder="EX: FRETEGRATIS" style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '2px solid #f1f5f9', fontSize: '14px', fontWeight: '900', color: '#ef4444', outline: 'none' }} value={novoCupom.codigo} onChange={e => setNovoCupom({...novoCupom, codigo: e.target.value.toUpperCase()})} required />
              </div>

              <div style={{ position: 'relative' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#64748b', marginBottom: '5px' }}>CLIENTE ESPECÍFICO (OPCIONAL)</label>
                <div style={{ position: 'relative' }}>
                  <Search size={14} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}/>
                  <input 
                    type="text" 
                    placeholder="Nome ou CPF..."
                    style={{ width: '100%', padding: '12px 12px 12px 35px', borderRadius: '12px', border: '2px solid #f1f5f9', outline: 'none', fontSize: '13px' }}
                    value={buscaClienteModal}
                    onChange={(e) => {
                      setBuscaClienteModal(e.target.value);
                      if (!e.target.value) setNovoCupom({...novoCupom, cliente_id: ''});
                    }}
                  />
                </div>
                {buscaClienteModal && !novoCupom.cliente_id && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', borderRadius: '12px', boxShadow: '0 10px 20px rgba(0,0,0,0.1)', zIndex: 10, border: '1px solid #e2e8f0', marginTop: '5px' }}>
                    {clientesFiltrados.map(c => {
                      const cpfCliente = c.cpf || c.documento || c.nu_cpf || 'Não informado';
                      return (
                        <div 
                          key={c.id} 
                          onClick={() => { 
                            setNovoCupom({...novoCupom, cliente_id: c.id}); 
                            setBuscaClienteModal(c.nome); 
                          }} 
                          style={{ padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '12px' }}
                        >
                          <b>{c.nome}</b> <br/> 
                          <small>📞 {c.whatsapp || 'Sem tel'}</small><br/>
                          <small>📄 CPF: {cpfCliente}</small>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#64748b', marginBottom: '5px' }}>TIPO</label>
                  <select 
                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '2px solid #f1f5f9', background: '#fff', fontWeight: '700', fontSize: '13px' }} 
                    value={novoCupom.tipo} 
                    onChange={e => setNovoCupom({...novoCupom, tipo: e.target.value, valor: e.target.value === 'frete_gratis' ? '0' : novoCupom.valor})}
                  >
                    <option value="porcentagem">% Porcentagem</option>
                    <option value="fixo">R$ Valor Fixo</option>
                    <option value="frete_gratis">🚚 Frete Grátis</option>
                  </select>
                </div>
                {novoCupom.tipo !== 'frete_gratis' && (
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#64748b', marginBottom: '5px' }}>VALOR</label>
                    <input type="number" style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '2px solid #f1f5f9', fontWeight: '900', fontSize: '13px' }} value={novoCupom.valor} onChange={e => setNovoCupom({...novoCupom, valor: e.target.value})} required />
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#64748b', marginBottom: '5px' }}>MÍNIMO (R$)</label>
                  <input type="number" style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '2px solid #f1f5f9', fontSize: '13px' }} value={novoCupom.valor_minimo} onChange={e => setNovoCupom({...novoCupom, valor_minimo: e.target.value})} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#64748b', marginBottom: '5px' }}>LIMITE USOS</label>
                  <input type="number" style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '2px solid #f1f5f9', fontSize: '13px' }} value={novoCupom.limite_usos} onChange={e => setNovoCupom({...novoCupom, limite_usos: e.target.value})} />
                </div>
              </div>

              <button type="submit" style={{ width: '100%', padding: '15px', borderRadius: '15px', border: 'none', background: '#0f172a', color: '#fff', fontWeight: '900', fontSize: '15px', cursor: 'pointer', marginTop: '10px' }}>
                {isEditing ? 'Salvar Alterações' : 'Criar Cupom Agora'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DE DELETAR */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', padding: '30px', borderRadius: '24px', width: '90%', maxWidth: '350px', textAlign: 'center' }}>
            <AlertCircle size={40} color="#ef4444" style={{ margin: '0 auto 15px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '900' }}>Excluir Cupom?</h3>
            <p style={{ color: '#64748b', fontSize: '14px', marginTop: '5px' }}>Essa ação não pode ser desfeita.</p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '25px' }}>
              <button onClick={() => setShowDeleteModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', background: '#f1f5f9', fontWeight: '800' }}>Voltar</button>
              <button onClick={confirmarExclusao} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', background: '#ef4444', color: '#fff', fontWeight: '800' }}>Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cupons;